import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Skeleton } from '@mono/ui-components';
import { Link } from 'react-router-dom';
import { useProgramFinancialSummary } from '../../../../components/delivery/widgets/useProgramFinancialSummary';
import { cn } from '../../../../lib/utils';
/**
 * The program-home verdict block (LAYOUT-SPEC §2A): the single most important
 * number, oversized — the SDS audit score. Sourced from the same KPI snapshot
 * the KPI spine reads (audit points passed ÷ total, 8/8 subs / 10/10 primes),
 * so there is no second data model to keep in sync.
 */ export function AuditVerdict({ programId }) {
    const { data, isLoading, isError } = useProgramFinancialSummary(programId);
    const auditsHref = `/delivery/programs/${programId}/audits`;
    if (isLoading) {
        return /*#__PURE__*/ _jsx(Skeleton, {
            className: "h-40 w-full rounded-lg"
        });
    }
    const kpi = data?.kpiSnapshot;
    const hasAudit = !isError && !!kpi?.hasAudit && (kpi?.latestAuditTotal ?? 0) > 0;
    const passed = kpi?.latestAuditPass ?? 0;
    const total = kpi?.latestAuditTotal ?? 0;
    const complete = hasAudit && passed >= total;
    return /*#__PURE__*/ _jsx("section", {
        className: "rounded-lg bg-muted/60 p-6",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "grid gap-7 sm:grid-cols-[190px_minmax(0,1fr)]",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                            children: "SDS Audit"
                        }),
                        hasAudit ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mt-2.5 flex items-baseline gap-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: cn('font-display text-[46px] font-normal leading-none tabular-nums', complete ? 'text-success' : 'text-danger'),
                                            children: passed
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "font-display text-[22px] font-normal text-muted-foreground",
                                            children: [
                                                "/ ",
                                                total
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "mt-1.5 text-[11.5px] leading-normal text-muted-foreground",
                                    children: "points passing"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "mt-3.5 flex max-w-[150px] gap-[3px]",
                                    children: Array.from({
                                        length: total
                                    }).map((_, i)=>/*#__PURE__*/ _jsx("span", {
                                            className: cn('h-1 flex-1 rounded-full', i < passed ? complete ? 'bg-success' : 'bg-danger' : 'bg-muted-foreground/25')
                                        }, i))
                                })
                            ]
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "mt-2.5 font-display text-[34px] font-normal leading-none text-muted-foreground",
                                    children: "—"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "mt-2 text-[11.5px] leading-normal text-muted-foreground",
                                    children: "No audit on record yet"
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "min-w-0",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-[12.5px] leading-relaxed text-muted-foreground",
                            children: "The SDS audit scores the program against the delivery-standards checklist each month. Non-compliant items become corrections, due within one week."
                        }),
                        /*#__PURE__*/ _jsxs(Link, {
                            className: "mt-3 inline-flex items-center gap-1 text-[12.5px] font-medium text-info hover:underline",
                            to: auditsHref,
                            children: [
                                hasAudit ? 'Review the audit' : 'Run an audit',
                                " →"
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
